import { motion } from "framer-motion";
import {
  Share2,
  Megaphone,
  PenLine,
  Palette,
  BookOpen,
  TrendingUp,
  ShoppingCart,
  Bot,
  Workflow,
  ExternalLink,
  type LucideIcon,
} from "lucide-react";
import SpotlightCard from "@/components/services/SpotlightCard";
import PlatformPreview from "@/components/services/PlatformPreview";

interface ServiceItem {
  icon: LucideIcon;
  title: string;
  description: string;
}

const marketingServices: ServiceItem[] = [
  {
    icon: Share2,
    title: "Gestão de Redes Sociais",
    description: "Posicionamento digital consistente que transforma seguidores em clientes fiéis.",
  },
  {
    icon: Megaphone,
    title: "Estratégia de Mídias",
    description: "Planejamento de canais e formatos alinhado aos objetivos do seu negócio.",
  },
  {
    icon: PenLine,
    title: "Copywriting",
    description: "Textos persuasivos que conectam sua marca ao público e geram conversão.",
  },
  {
    icon: Palette,
    title: "Design Visual",
    description: "Identidade visual profissional que transmite confiança e autoridade.",
  },
  {
    icon: BookOpen,
    title: "Inbound & E-books",
    description: "Materiais ricos e conteúdo educativo que posicionam sua marca como referência.",
  },
];

const growthServices: ServiceItem[] = [
  {
    icon: TrendingUp,
    title: "Tráfego Pago de Alta Performance",
    description: "Campanhas otimizadas para atrair clientes qualificados e multiplicar vendas.",
  },
  {
    icon: ShoppingCart,
    title: "Criação de E-Commerce",
    description: "Lojas virtuais completas, pensadas para converter o consumidor moderno.",
  },
];

const aiServices: ServiceItem[] = [
  {
    icon: Bot,
    title: "Chatbots Inteligentes de Atendimento",
    description: "Agentes de IA que atendem, qualificam e convertem clientes 24/7.",
  },
  {
    icon: Workflow,
    title: "Automação de Fluxos com IA",
    description: "Processos automatizados que eliminam tarefas repetitivas e escalam operações.",
  },
];

const platforms = [
  {
    name: "Abilly PDV",
    url: "https://abillypdv.com.br",
    description: "Sistema de ponto de venda completo para o varejo.",
    variant: "pdv" as const,
  },
  {
    name: "Finance Simples",
    url: "https://financesimples.com.br",
    description: "Gestão financeira descomplicada para o seu negócio.",
    variant: "finance" as const,
  },
];

const container = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.12 } },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" as const } },
};

const CategoryBadge = ({ children }: { children: React.ReactNode }) => (
  <span className="inline-block rounded-full border border-primary/25 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-widest text-primary">
    {children}
  </span>
);

const ServiceRow = ({ service }: { service: ServiceItem }) => {
  const Icon = service.icon;
  return (
    <li className="flex items-start gap-3 rounded-xl p-2 transition-colors duration-300 hover:bg-secondary/70">
      <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
        <Icon size={18} />
      </span>
      <div>
        <p className="font-heading font-semibold text-foreground">{service.title}</p>
        <p className="text-sm text-muted-foreground">{service.description}</p>
      </div>
    </li>
  );
};

const Services = () => {
  return (
    <section id="servicos" className="py-24 bg-section-bg">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16 space-y-4 animate-fade-in">
          <span className="inline-block text-primary font-medium text-sm tracking-widest uppercase">
            O que fazemos
          </span>
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-foreground text-balance">
            Nossos <span className="bg-hero-gradient bg-clip-text text-transparent">Serviços</span>
          </h2>
          <div className="w-20 h-1 bg-hero-gradient mx-auto rounded-full" />
          <p className="text-muted-foreground text-lg">
            Do marketing estratégico à inteligência artificial: soluções completas para escalar o seu negócio.
          </p>
        </div>

        <motion.div
          variants={container}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          className="grid grid-cols-1 gap-6 lg:grid-cols-6"
        >
          {/* Marketing & Conteúdo Estratégico — card grande */}
          <motion.div variants={item} className="lg:col-span-4">
            <SpotlightCard className="h-full p-6 md:p-8">
              <CategoryBadge>Marketing & Conteúdo Estratégico</CategoryBadge>
              <h3 className="mt-4 font-heading text-2xl font-bold text-foreground md:text-3xl">
                Conteúdo que constrói autoridade
              </h3>
              <ul className="mt-6 grid grid-cols-1 gap-2 sm:grid-cols-2">
                {marketingServices.map((service) => (
                  <ServiceRow key={service.title} service={service} />
                ))}
              </ul>
            </SpotlightCard>
          </motion.div>

          {/* Growth & Vendas */}
          <motion.div variants={item} className="lg:col-span-2">
            <SpotlightCard className="h-full p-6 md:p-8">
              <CategoryBadge>Growth & Vendas</CategoryBadge>
              <h3 className="mt-4 font-heading text-2xl font-bold text-foreground">
                Aceleração de resultados
              </h3>
              <ul className="mt-6 space-y-2">
                {growthServices.map((service) => (
                  <ServiceRow key={service.title} service={service} />
                ))}
              </ul>
            </SpotlightCard>
          </motion.div>

          {/* IA & Agentes */}
          <motion.div variants={item} className="lg:col-span-2">
            <SpotlightCard className="h-full p-6 md:p-8">
              <CategoryBadge>Inteligência Artificial & Agentes</CategoryBadge>
              <h3 className="mt-4 font-heading text-2xl font-bold text-foreground">
                Automação inteligente
              </h3>
              <ul className="mt-6 space-y-2">
                {aiServices.map((service) => (
                  <ServiceRow key={service.title} service={service} />
                ))}
              </ul>
            </SpotlightCard>
          </motion.div>

          {/* Plataformas Próprias — card grande com mini-previews */}
          <motion.div variants={item} className="lg:col-span-4">
            <SpotlightCard className="h-full p-6 md:p-8">
              <CategoryBadge>Plataformas Próprias</CategoryBadge>
              <h3 className="mt-4 font-heading text-2xl font-bold text-foreground md:text-3xl">
                Tecnologia desenvolvida pela nossa equipe
              </h3>
              <p className="mt-2 text-muted-foreground">
                Softwares criados internamente para resolver problemas reais dos nossos clientes.
              </p>
              <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
                {platforms.map((platform) => (
                  <a
                    key={platform.name}
                    href={platform.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="group/platform block rounded-xl border border-border bg-card/60 p-5 transition-all duration-300 hover:-translate-y-1 hover:border-primary/40 hover:shadow-xl"
                  >
                    <div className="flex items-center justify-between">
                      <p className="font-heading text-lg font-bold text-foreground transition-colors group-hover/platform:text-primary">
                        {platform.name}
                      </p>
                      <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10 text-primary transition-all duration-300 group-hover/platform:bg-primary group-hover/platform:text-primary-foreground">
                        <ExternalLink size={15} />
                      </span>
                    </div>
                    <p className="mt-1 text-sm text-muted-foreground">{platform.description}</p>
                    <PlatformPreview variant={platform.variant} />
                  </a>
                ))}
              </div>
            </SpotlightCard>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default Services;
